// Images
import ZigZagThumbnail from '@/images/patterns/thumbnails/zigzag.svg';
import LinesThumbnail from '@/images/patterns/thumbnails/lines.svg';
import RadialThumbnail from '@/images/patterns/thumbnails/radial.svg';
import EqualizerThumbnail from '@/images/patterns/thumbnails/equalizer.svg';
import NoneThumbnail from '@/images/patterns/thumbnails/none.svg';

import Dictionary from '@/types/dictionary';

export const DEFAULT_PORTRAIT_FRACTION = 0.33;
export const DEFAULT_LANDSCAPE_FRACTION = 0.4;

export const FALLBACK_COLOR = '#0E3955';

export const PROPOSAL_BANNER_STYLES = [
  {
    background: '#0e3955',
    patternColor: 'rgba(255,255,255,0.07)',
  },
  {
    background: '#393a5c',
    gradientRatio: 0.1,
    patternColor: 'rgba(255,255,255,0.07)',
  },
  {
    background: '#0587da',
    patternColor: 'rgba(255,255,255,0.07)',
  },
  {
    background: '#6236ff',
    gradientRatio: 0.1,
    patternColor: 'rgba(0,0,0,0.07)',
  },
  {
    background: '#541a9b',
    gradientRatio: 0.1,
    patternColor: 'rgba(255,255,255,0.07)',
  },
  {
    background: '#101b4b',
    gradientRatio: 0.1,
    patternColor: 'rgba(255,255,255,0.07)',
  },
  {
    background: '#721f37',
    gradientRatio: 0.1,
    patternColor: 'rgba(255,255,255,0.07)',
  },
  {
    background: '#ff6a20',
    patternColor: 'rgba(0,0,0,0.05)',
  },
  {
    background: '#f7b500',
    gradientRatio: 0.1,
    patternColor: 'rgba(0,0,0,0.07)',
  },
  {
    background: '#33694e',
    gradientRatio: 0.1,
    patternColor: 'rgba(255,255,255,0.07)',
  },
  {
    background: '#d4bd9a',
    gradientRatio: 0.1,
    patternColor: 'rgba(0,0,0,0.03)',
  },
  {
    background: '#6d7278',
    gradientRatio: 0.1,
    patternColor: 'rgba(0,0,0,0.05)',
  },
  {
    background: '#f2f2f2',
    gradientRatio: 0.1,
    patternColor: 'rgba(0,0,0,0.05)',
  },
  {
    background: '#ffffff',
    border: 'rgba(0,0,0,0.05)',
    gradientRatio: 0,
    patternColor: 'rgba(0,0,0,0.05)',
  },
];

export enum PROPOSAL_PATTERN {
  ZIGZAG = 'ZIGZAG',
  EQUALIZER = 'EQUALIZER',
  LINES = 'LINES',
  RADIAL = 'RADIAL',
  NONE = 'NONE',
}

export const THUMBNAIL: Dictionary<string> = {
  [PROPOSAL_PATTERN.ZIGZAG]: ZigZagThumbnail,
  [PROPOSAL_PATTERN.LINES]: LinesThumbnail,
  [PROPOSAL_PATTERN.RADIAL]: RadialThumbnail,
  [PROPOSAL_PATTERN.EQUALIZER]: EqualizerThumbnail,
  [PROPOSAL_PATTERN.NONE]: NoneThumbnail,
};

export enum PROPOSAL_PAPER_FORMAT {
  LETTER = 'Letter',
  A4 = 'A4',
  LEGAL = 'Legal',
}

export enum PROPOSAL_ORIENTATION {
  PORTRAIT = 'Portrait',
  LANDSCAPE = 'Landscape',
}

export enum PROPOSAL_OPTION_INPUT {
  RADIO = 'radio',
  TOGGLE = 'toggle',
  CHECKBOX = 'checkbox',
}

export const PROPOSAL_DISCLAIMERS = {
  LIPPER_LEADERS: 'LIPPER_LEADERS',
  LIPPER_AS_AT: 'LIPPER_AS_AT',
  NET_PERFORMANCE: 'NET_PERFORMANCE',
  NET_FUND_FEES_PERFORMANCE: 'NET_FUND_FEES_PERFORMANCE',
  PERFORMANCE_PERIOD: 'PERFORMANCE_PERIOD',
  RISK_PERIOD: 'RISK_PERIOD',
  RISK_PROFILE_MAY_DIFFER: 'RISK_PROFILE_MAY_DIFFER',
  HOLDINGS_AS_AT: 'HOLDINGS_AS_AT',
  ALLOCATIONS_AS_AT: 'ALLOCATIONS_AS_AT',
  DETAILED_ALLOCATIONS_AS_AT: 'DETAILED_ALLOCATIONS_AS_AT',
  CREDIT_QUALITY_AS_AT: 'CREDIT_QUALITY_AS_AT',
  FUNDATA_MERS: 'FUNDATA_MERS',
  OTHER_FIXED_INCOME_METRICS: 'OTHER_FIXED_INCOME_METRICS',
  MATURITY_AS_AT: 'MATURITY_AS_AT',
  FIXED_INCOME_REGION_AS_AT: 'FIXED_INCOME_REGION_AS_AT',
  SHORT_HISTORY_PORTFOLIOS: 'SHORT_HISTORY_PORTFOLIOS',
  CORRELATION_MATRIX_LOCATION: 'CORRELATION_MATRIX_LOCATION',
  RETURN_ON_EQUITY: 'RETURN_ON_EQUITY',
  EQUITY_OTHER_METRICS: 'EQUITY_OTHER_METRICS',
  EQUITY_STYLE: 'EQUITY_STYLE',
  MODIFIED_DURATION: 'MODIFIED_DURATION',
  FIXED_INCOME_STYLE: 'FIXED_INCOME_STYLE',
  FIXED_INCOME_SECTOR: 'FIXED_INCOME_SECTOR',
  INSTRUMENT_CUSTOM_DISCLAIMER: 'INSTRUMENT_CUSTOM_DISCLAIMER',
  HISTORICAL_SCENARIO_RETURN: 'HISTORICAL_SCENARIO_RETURN',
  HYPOTHETICAL_RETURN: 'HYPOTHETICAL_RETURN',
  DRAWDOWN_ANALYSIS: 'DRAWDOWN_ANALYSIS',
  ROLL_INTO_OTHER: 'ROLL_INTO_OTHER',
  ROLL_INTO_NOT_RATED: 'ROLL_INTO_NOT_RATED',
  BETA_DISCLAIMER: 'BETA_DISCLAIMER',
  INCOME_STREAM: 'INCOME_STREAM',
  BENCHMARKS: 'BENCHMARKS',
  BENCHMARKS_BREAKDOWN: 'BENCHMARKS_BREAKDOWN',
  UP_DOWN_MONTHS: 'UP_DOWN_MONTHS',
  CUSTOM_DISCLAIMER: 'CUSTOM_DISCLAIMER',
  NON_CLASSIFIED_EQUITY: 'NON_CLASSIFIED_EQUITY',
  CALCULATION_BENCHMARKS: 'CALCULATION_BENCHMARKS',
  BMO_GIF_MARKETING_DISCLAIMER: 'BMO_GIF_MARKETING_DISCLAIMER',
  CUSTOM_PROVIDER_RISK_RATING: 'CUSTOM_PROVIDER_RISK_RATING',
  CUSTOM_ALLOCATIONS: 'CUSTOM_ALLOCATIONS',
  YIELD_AS_AT: 'YIELD_AS_AT',
  YIELD_TO_MATURITY: 'YIELD_TO_MATURITY',
  ESG_OVERVIEW: 'ESG_OVERVIEW',
  ESG_CARBON_INTENSITY: 'ESG_CARBON_INTENSITY',
  ESG_IMPLIED_TEMPERATURE_RISE: 'ESG_IMPLIED_TEMPERATURE_RISE',
  SINCE_INCEPTION_DATE: 'SINCE_INCEPTION_DATE',
  SI_YOUNG_HOLDINGS: 'SI_YOUNG_HOLDINGS',
  COMMON_INCEPTION_DATE: 'COMMON_INCEPTION_DATE',
  TAX_ANALYSIS: 'TAX_ANALYSIS',
};

export const CORRELATION_MATRIX_BOUNDARIES = {
  INLINE_PORTRAIT: 5,
  MAX_PORTRAIT: 33,
};

export enum PROPOSAL_ITEM_PURPOSE {
  NONE = 'proposalItemPurpose_none',
  CURRENT = 'proposalItemPurpose_current',
  RECOMMENDED = 'proposalItemPurpose_recommended',
}

export enum PROPOSAL_SECTION_KEYS {
  RISK_PROFILE = 'RISK_PROFILE',
  ASSET_ALLOCATION_TIER_ONE_ONLY = 'ASSET_ALLOCATION_TIER_ONE_ONLY',
  // Add others when required, for now just these two are needed
}
